import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { FormControlLabel, FormGroup, Switch } from '@mui/material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LayersIcon from '@mui/icons-material/Layers';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import moment from 'moment';

import { CompanyDetail } from '../../interfaces/companyDetail.interface';
import { updateCompanyBoolean } from '../../services/company';
import { USER_TYPES } from '../../services/user';
import { COMPANY_TYPES } from '../../services/vendor';

function copyText(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

interface RowDef {
  label: string;
  value: string;
}

function DataRow({ label, value, index }: RowDef & { index: number }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        py: 0.75,
        bgcolor: index % 2 === 0 ? 'rgba(0,0,0,0.025)' : 'transparent',
      }}
    >
      <Typography variant="body2" sx={{ color: 'text.secondary', width: '35%', flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flexGrow: 1, whiteSpace: 'pre-line' }}>
        {value}
      </Typography>
      <IconButton size="small" sx={{ p: 0.25, ml: 0.5 }} onClick={() => copyText(value)} aria-label={`Copy ${label}`}>
        <ContentCopyOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
      </IconButton>
    </Box>
  );
}

function AdminSection({
  icon,
  title,
  rows,
  footer,
  onMenuEdit,
  menuEditLabel,
}: {
  icon: React.ReactNode;
  title: string;
  rows: RowDef[];
  footer?: React.ReactNode;
  onMenuEdit?: () => void;
  menuEditLabel?: string;
}) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = rows.map(({ label, value }) => `${label}: ${value}`).join('\n');
    copyText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1, fontSize: '0.95rem' }}>
          {title}
        </Typography>
        <Tooltip
          title={`${title} copied`}
          open={copied}
          disableFocusListener
          disableHoverListener
          disableTouchListener
        >
          <IconButton size="small" onClick={handleCopy} aria-label={`Copy ${title}`}>
            <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        {onMenuEdit && (
          <>
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="Actions">
              <MoreHorizIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={() => { onMenuEdit(); setMenuAnchor(null); }}>
                {menuEditLabel ?? 'Edit'}
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Box>
        {rows.map((row, i) => (
          <DataRow key={row.label} label={row.label} value={row.value} index={i} />
        ))}
      </Box>
      {footer && <Box sx={{ pt: 0.5 }}>{footer}</Box>}
    </Box>
  );
}

interface AdminTabProps {
  companyId: number;
  detail: CompanyDetail | null;
  userType?: string;
  onEditCompany?: () => void;
  onEditLegalContact?: () => void;
}

export default function AdminTab({ companyId, detail, userType, onEditCompany, onEditLegalContact }: AdminTabProps) {
  const [isGold, setIsGold] = useState(detail?.contentOwnerLevel === 2);

  const updateGold = () => {
    setIsGold((prev) => {
      updateCompanyBoolean({ id: companyId, contentOwnerLevel: prev ? 1 : 2 });
      return !prev;
    });
  };

  if (!detail) return null;

  const legalContact = detail.companyLegalContacts?.[0];
  const isPaid = detail.billingStatus === 'active';

  const companyRows: RowDef[] = [
    detail.grossRevenue ? { label: 'Annual Gross Revenue', value: detail.grossRevenue } : null,
    detail.employeeCount ? { label: 'Employee Count', value: detail.employeeCount } : null,
    detail.contentOwnerLevel != null ? { label: 'Membership Level', value: String(detail.contentOwnerLevel) } : null,
  ].filter(Boolean) as RowDef[];

  const legalContactRows: RowDef[] = legalContact ? [
    legalContact.name ? { label: 'Name', value: legalContact.name } : null,
    legalContact.email ? { label: 'Email', value: legalContact.email } : null,
  ].filter(Boolean) as RowDef[] : [];

  const billingAddressValue = [
    detail.billingContact?.name,
    detail.billingStatementName,
    detail.billingContact?.address,
    detail.billingContact?.address2,
    detail.billingContact?.address3,
  ].filter(Boolean).join('\n');

  const billingContactValue = [
    detail.billingContact?.city,
    detail.billingContact?.phoneNumber,
  ].filter(Boolean).join('\n');

  const billingRows: RowDef[] = [
    billingAddressValue ? { label: 'Address', value: billingAddressValue } : null,
    billingContactValue ? { label: 'Contact', value: billingContactValue } : null,
  ].filter(Boolean) as RowDef[];

  const legalAddressValue = legalContact?.address ?? '';

  const legalAddressRows: RowDef[] = legalAddressValue
    ? [{ label: 'Address', value: legalAddressValue }]
    : [];

  const showGoldToggle =
    userType === USER_TYPES.TPN_ADMIN && detail.type === COMPANY_TYPES.CONTENT_OWNER;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 4, px: 2, py: 1, mb: 2, bgcolor: '#F8F9FD', borderRadius: 1 }}>
        <Typography variant="body2">
          <strong>Paid Status:</strong>{' '}{isPaid ? 'Paid' : 'Inactive'}
        </Typography>
        {detail.markedAsPaidAt && (
          <Typography variant="body2">
            <strong>Start Date:</strong>{' '}{moment(detail.markedAsPaidAt).format('MM/DD/YYYY')}
          </Typography>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AdminSection
            icon={<LayersIcon />}
            title="Company Details"
            rows={companyRows}
            onMenuEdit={onEditCompany}
            menuEditLabel="Edit Company"
            footer={showGoldToggle ? (
              <FormGroup>
                <FormControlLabel
                  control={<Switch checked={isGold} onChange={updateGold} />}
                  label="Gold Member"
                />
              </FormGroup>
            ) : undefined}
          />
        </Grid>

        {legalContact && (
          <Grid item xs={12} md={6}>
            <AdminSection
              icon={<AccountCircleOutlinedIcon />}
              title="Legal Contact"
              rows={legalContactRows}
              onMenuEdit={onEditLegalContact}
              menuEditLabel="Edit Legal Contact"
            />
          </Grid>
        )}

        {detail.billingContact && (
          <Grid item xs={12} md={6}>
            <AdminSection
              icon={<BallotOutlinedIcon />}
              title="Billing Address"
              rows={billingRows}
              onMenuEdit={onEditCompany}
              menuEditLabel="Edit Company"
            />
          </Grid>
        )}

        {legalAddressValue && (
          <Grid item xs={12} md={6}>
            <AdminSection
              icon={<ArticleOutlinedIcon />}
              title="Legal Address"
              rows={legalAddressRows}
              onMenuEdit={onEditLegalContact}
              menuEditLabel="Edit Legal Contact"
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
